import { skipToken } from '@reduxjs/toolkit/query';
import { BenefitTypes } from '@strata/constants';
import { conditionalString, formatDate } from '@strata/helper-functions';
import { DollarAmount, formatDollarAmount } from '@strata/react-dollar-amount';
import { FormSectionWithRows } from '@strata/react-form-section';
import {
    DetailLabel,
    DetailLabelList,
    DetailLabelValuePair,
    DetailValue,
} from '@strata/react-label-value-pairs';
import { ClaimStatusIndicator } from '@strata/react-status-indicators';

import { ClaimUomMap } from 'services/MedicalBillingConstants';
import { addQtyUnits } from 'services/ProductConfigHelpers';
import {
    type ClaimDetails,
    formatCodeDescLabel,
    formatDiagnosisLabel,
} from 'store/api/medbillingApi';
import {
    type DiagnosisCodeOption,
    useGetEligiblePricesForOidQuery,
    useGetProductCodeArrQuery,
} from 'store/api/referenceApi';
import { type ReferralDetails } from 'store/api/treatmentApi';

interface ServiceProps {
    service: ClaimDetails['services'][0];
    index: number;
    diagnosesList?: DiagnosisCodeOption[];
    referralDetails?: ReferralDetails;
}

export const Service = ({
    service,
    index,
    diagnosesList = undefined,
    referralDetails = undefined,
    // eslint-disable-next-line complexity
}: ServiceProps) => {
    const { data: productCodeArr } = useGetProductCodeArrQuery();

    const { data: unitPrices } = useGetEligiblePricesForOidQuery(
        referralDetails?.referentPriceType && referralDetails.priceSource
            ? {
                  oid: referralDetails.productServiceOid,
                  referentPriceType: referralDetails.referentPriceType,
                  isBrand: referralDetails.isBrand,
                  priceSource: referralDetails.priceSource,
                  priceDate: service.dateOfService,
                  benefitType: referralDetails.benefit,
              }
            : skipToken,
    );

    const isPharmacyBenefit = referralDetails?.benefit === BenefitTypes.PHARMACY;
    const isMedicalBenefit = !isPharmacyBenefit;

    const unitPrice = unitPrices?.find(
        (price) => price.code === (isPharmacyBenefit ? service.ndc : service.procedureCode),
    )?.price;

    return (
        <FormSectionWithRows
            sectionTitle={`Service ${index + 1}`}
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '30%' }}
                        label="Date(s) Of Service"
                        value={`${formatDate(service.dateOfService)} - ${formatDate(
                            service.dateOfServiceEnd,
                        )}`}
                    />
                    <DetailLabelValuePair
                        style={{ width: '35%' }}
                        label={`Procedure${conditionalString(isMedicalBenefit, ' (Unit Price)')}`}
                        value={`${service.procedureCodeDisplayText}${conditionalString(
                            isMedicalBenefit && !!unitPrice,
                            ` (${formatDollarAmount(unitPrice, true, false)})`,
                        )}`}
                    />
                    <DetailLabelValuePair
                        style={{ width: '35%' }}
                        label="Modifier Code(s)"
                        value={service.modifierCodes?.join(', ')}
                    />
                </>
            }
            rowTwoContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '30%' }}
                        label={`NDC${conditionalString(isPharmacyBenefit, ' (Unit Price)')}`}
                        value={`${formatCodeDescLabel(
                            service.ndc,
                            service.ndc &&
                                productCodeArr?.find((pc) => pc.code === service.ndc)?.description,
                            true,
                        )}${conditionalString(
                            isPharmacyBenefit && !!unitPrice,
                            ` (${formatDollarAmount(unitPrice, true, false)})`,
                        )}`}
                    />
                    <DetailLabelList
                        style={{ width: '35%' }}
                        label="Diagnoses"
                        values={service.diagnoses?.map((dx) =>
                            formatDiagnosisLabel(dx, diagnosesList),
                        )}
                    />
                    <DetailLabelValuePair
                        style={{ width: '35%' }}
                        label="Dispensed Qty"
                        value={addQtyUnits(
                            service?.dispensedQty,
                            service.dispensedQtyUom && ClaimUomMap[service.dispensedQtyUom],
                        )}
                    />
                </>
            }
            rowThreeContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '30%' }}
                        label="Billing Qty"
                        value={addQtyUnits(service?.billingQty, ClaimUomMap[service.billingQtyUom])}
                    />
                    <DetailLabelValuePair
                        style={{ width: '35%' }}
                        label="Submitted Amount"
                        value={
                            <DollarAmount amount={service.submittedAmount} large showZeroValue />
                        }
                    />
                    <DetailLabelValuePair
                        style={{ width: '35%' }}
                        label="Paid Amount"
                        value={<DollarAmount amount={service.total} large showZeroValue />}
                    />
                </>
            }
            rowFourContent={
                <>
                    <div style={{ width: '30%' }}>
                        <DetailLabel label="Status" />
                        {service?.serviceStatus ? (
                            <ClaimStatusIndicator status={service.serviceStatus} />
                        ) : (
                            <DetailValue />
                        )}
                    </div>
                    <DetailLabelList
                        style={{ width: '35%' }}
                        label="Claim Status Codes"
                        values={service.serviceStatusCodes}
                    />
                    <DetailLabelList
                        style={{ width: '35%' }}
                        label="Secondary Claim Status Codes"
                        values={service.secondaryServiceStatusCodes}
                    />
                </>
            }
        />
    );
};
